import { useEffect, useState } from 'react';
import { NotebookPen, Save, Trash2 } from 'lucide-react';
import { Button, EmptyState, LoadingState, SectionHeading } from '../components/ui';
import { loadLocalNotes, saveLocalNotes, type PersonalNote } from '../lib/studentUtilities';

const PREVIEW_LENGTH = 140;

export function StudentNotes() {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<PersonalNote[]>([]);
  const [draft, setDraft] = useState({ title: '', body: '' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let active = true;
    void Promise.resolve(loadLocalNotes()).then((stored) => { if (active) { setNotes(stored); setLoading(false); } });
    return () => { active = false; };
  }, []);

  const persist = (next: PersonalNote[]) => { setNotes(next); saveLocalNotes(next); };

  const add = () => {
    if (!draft.title.trim() && !draft.body.trim()) return;
    persist([{ id: crypto.randomUUID(), title: draft.title.trim() || 'Untitled note', body: draft.body.trim(), updatedAt: new Date().toISOString() }, ...notes]);
    setDraft({ title: '', body: '' });
  };

  const remove = (id: string) => persist(notes.filter((note) => note.id !== id));

  const toggleExpanded = (id: string) => setExpanded((previous) => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (loading) return <LoadingState />;
  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><NotebookPen size={28} /></div><div><h1>Notes</h1><p>Your personal study notes.</p></div></section>
    <div className="local-warning">Stored only on this device — notes are not synced to your account and will not appear on other devices or browsers.</div>
    <section className="detail-section">
      <SectionHeading title="New note" />
      <div className="composer-card">
        <label>Title<input value={draft.title} onChange={(event) => setDraft((value) => ({ ...value, title: event.target.value }))} placeholder="e.g. Shotha chikitsa key points" /></label>
        <label>Note<textarea rows={4} value={draft.body} onChange={(event) => setDraft((value) => ({ ...value, body: event.target.value }))} placeholder="Write your note…" /></label>
        <Button icon={Save} onClick={add}>Save note</Button>
      </div>
    </section>
    <section className="detail-section">
      <SectionHeading title={`My notes · ${notes.length}`} />
      {notes.length ? <div className="notes-list">{notes.map((note) => {
        const isLong = note.body.length > PREVIEW_LENGTH;
        const isOpen = expanded.has(note.id);
        return <article className="solution-card" key={note.id}>
          <div className="question-number">{note.title}
            <button className="icon-danger" aria-label="Delete note" onClick={() => remove(note.id)}><Trash2 size={15} /></button>
          </div>
          <p className="detail-description">{isLong && !isOpen ? `${note.body.slice(0, PREVIEW_LENGTH).trimEnd()}…` : note.body}</p>
          {isLong && <button className="text-button more-toggle" onClick={() => toggleExpanded(note.id)}>{isOpen ? 'Less' : 'More'}</button>}
          <small className="note-date">Saved {new Date(note.updatedAt).toLocaleString()}</small>
        </article>;
      })}</div>
      : <EmptyState icon={NotebookPen} title="No notes yet" description="Capture quick revision points while you study." />}
    </section>
  </div>;
}
